import { App, Alert, Form, Input, Modal, Select, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { rolesApi, usersApi, type CreateUserFromStaffBody } from '@/api/users';
import { extractError } from '@/shared/lib/error-message';

interface StaffMinimal {
  id: number;
  firstName: string;
  lastName: string;
}

interface FormShape {
  username: string;
  roleId: number;
}

export function suggestUsername(s: StaffMinimal): string {
  const slug = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, '');
  const first = slug(s.firstName);
  const last = slug(s.lastName);
  if (first && last) return `${first}.${last}`;
  return first || last || `staff${s.id}`;
}

interface Props {
  staff: StaffMinimal | null;
  open: boolean;
  onClose: () => void;
}

export function CreateUserFromStaffModal({ staff, open, onClose }: Props) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<FormShape>();

  const rolesQuery = useQuery({
    queryKey: ['users', 'roles'],
    queryFn: rolesApi.list,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (open && staff) {
      form.setFieldsValue({
        username: suggestUsername(staff),
        roleId: undefined as unknown as number,
      });
    }
  }, [open, staff, form]);

  const create = useMutation({
    mutationFn: (body: CreateUserFromStaffBody) => usersApi.createFromStaff(body),
    onSuccess: (u) => {
      message.success(`User "${u.username}" created — they must change password on first login`);
      qc.invalidateQueries({ queryKey: ['hr', 'staff'] });
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
      onClose();
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e, 'Failed to create user')),
  });

  const submit = (values: FormShape) => {
    if (!staff) return;
    create.mutate({
      staffId: staff.id,
      username: values.username.trim(),
      roleId: values.roleId,
    });
  };

  const fullName = staff ? `${staff.firstName} ${staff.lastName}`.trim() : '';

  return (
    <Modal
      title={staff ? `Create login for ${fullName}` : 'Create login'}
      open={open}
      onCancel={() => {
        onClose();
        form.resetFields();
      }}
      onOk={() => form.submit()}
      okText="Create user"
      confirmLoading={create.isPending}
      destroyOnHidden
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="A default password is set from system settings"
        description="The user will be required to change it on first login. Update the default in Setup → System settings (key: default_user_password)."
      />
      <Form<FormShape> form={form} layout="vertical" onFinish={submit}>
        <Form.Item
          label="Username"
          name="username"
          rules={[
            { required: true, message: 'Username is required' },
            { min: 3, max: 50, message: 'Username must be 3–50 characters' },
          ]}
          extra={
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Will be used to sign in. Lowercase, no spaces. Suggested from staff name.
            </Typography.Text>
          }
        >
          <Input autoFocus placeholder="firstname.lastname" autoComplete="off" />
        </Form.Item>
        <Form.Item
          label="Role"
          name="roleId"
          rules={[{ required: true, message: 'Pick a role' }]}
        >
          <Select
            placeholder="Select role"
            loading={rolesQuery.isLoading}
            options={(rolesQuery.data ?? [])
              .filter((r) => r.active)
              .map((r) => ({ value: r.id, label: r.name }))}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
